(ns ycweb.app.website.devtools.devtools
  "The /dev-tools.htm page: which tracking sandbox this box talks to, the
   browser switches (GTM loader, tracking inspector) and the console cheat
   sheet. Staff only; the server decides that in the api handler."
  (:require
   [rum.core :as rum]
   [ycweb.app.website.tracking :as tracking]
   [ycweb.app.website.website :as website]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.flow :as flow]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   #?@(:cljs [[goog.object :as gobj]])))

;; ---------------------------------------------------------------------------
;; Browser state the page reads and writes

(defn- gtm-off?
  "PHP skips the GTM loader off production when the cookie yc_gtm=off is
   present. Server side the cookie arrives with the render request."
  [app-atom]
  #?(:clj (= "off" (get-in @app-atom [:page :cookies :yc_gtm]))
     :cljs (boolean (re-find #"(?:^|;\s*)yc_gtm=off(?:;|$)" (str (.-cookie js/document))))))

(defn- set-gtm-off! [off?]
  #?(:cljs
     (set! (.-cookie js/document)
       (if off?
         "yc_gtm=off; path=/; max-age=31536000; SameSite=Lax"
         "yc_gtm=; path=/; max-age=0; SameSite=Lax"))))

(defn- refresh-browser-state! [app-atom]
  (swap! app-atom update :dev-tools assoc
    :inspector (tracking/inspector-status-map)
    :gtm-off? (gtm-off? app-atom)))

;; ---------------------------------------------------------------------------
;; Page

(defn- row [label value]
  [:p.yc-paragraph {:key label}
   [:strong (str label ": ")]
   value])

(defn- yes-no [value]
  (if value "yes" "no"))

(defn- switch-button [app-atom text action]
  (buttons/button-web text
    {:style :secondary
     :size :small
     :on-click #(grid/dispatch-action app-atom action {})}))

(rum/defc environment-section < rum/reactive [app-atom]
  (let [{:keys [dev-mode? gtm ga4 meta inspector]} (cmn/curact app-atom [:dev-tools])]
    [:div.u-margin-bottom-30
     (headings/heading "Where this box sends tracking" {:type :h2 :size :small})
     (row "Page environment" (or (:environment inspector) "unknown until the page mounts"))
     (row "Dev mode on the server" (yes-no dev-mode?))
     (row "GTM container"
       [:span (:container-id gtm)
        (when (:sandbox? gtm) " (sandbox: GA4 only, ad tags paused)")
        (when (:env-params? gtm) " (preview environment params in Consul)")])
     (row "GA4 property" (or (u/presence (:property-id ga4)) "not known for this container"))
     (row "Meta dataset" (or (u/presence (:dataset-id meta)) "none configured: CAPI logs only"))
     (row "Meta test event code"
       (if (u/presence (:test-event-code meta))
         (str (:test-event-code meta) " (CAPI events go to the Test events tab while it is open)")
         "none: CAPI events are live for this dataset"))]))

(rum/defc switches-section < rum/reactive [app-atom]
  (let [{:keys [gtm-off? inspector]} (cmn/curact app-atom [:dev-tools])]
    [:div.u-margin-bottom-30
     (headings/heading "Browser switches" {:type :h2 :size :small})
     [:div.u-margin-bottom-20
      (row "GTM loader" (if gtm-off? "off (cookie yc_gtm=off)" "on"))
      [:p.yc-paragraph "Off means PHP does not emit the gtm.js snippet on local and staging; the dataLayer is still written. Reload any page after switching."]
      (switch-button app-atom (if gtm-off? "Turn GTM back on" "Turn GTM off") :web.dev-tools/toggle-gtm)]
     [:div
      (row "Tracking inspector"
        (cond
          (nil? inspector) "unknown until the page mounts"
          (:enabled? inspector) "on"
          :else "off"))
      (row "Override" (or (:override inspector) "none (follows the page environment)"))
      (row "Pushes recorded on this page" (str (or (:count inspector) 0)))
      (row "GTM containers loaded" (if (seq (:containers inspector)) (apply str (interpose ", " (:containers inspector))) "none"))
      [:p.yc-paragraph "The inspector records every dataLayer push the SPA attempts, with or without GTM, and logs them to the console when on. On by default everywhere except production."]
      (switch-button app-atom (if (:enabled? inspector) "Turn inspector off" "Turn inspector on") :web.dev-tools/toggle-inspector)
      " "
      (switch-button app-atom "Follow the environment again" :web.dev-tools/reset-inspector)]]))

(defn- feedback! [app-atom k message]
  (swap! app-atom assoc-in [:dev-tools :feedback k] message))

(defn- count-pushes [] #?(:cljs (count (tracking/inspector-events->js)) :clj 0))

(defn- datalayer-events
  "GTM's own array, minus the gtm.* housekeeping entries."
  []
  #?(:cljs
     (when (exists? js/dataLayer)
       (.filter js/dataLayer (fn [e] (when-let [n (gobj/get e "event")] (not (re-find #"^gtm\." n))))))))

(defn- remove-storage-keys!
  "Remove every key matching re from a web storage; returns how many went."
  [storage re]
  #?(:cljs
     (try
       (let [ks (vec (for [i (range (.-length storage))
                           :let [k (.key storage i)]
                           :when (re-find re k)]
                       k))]
         (doseq [k ks] (.removeItem storage k))
         (count ks))
       (catch :default _ 0))
     :clj 0))

(defmethod grid/action :web.dev-tools/log-events [app-atom _ _]
  #?(:cljs (js/console.table (tracking/inspector-events->js)))
  (feedback! app-atom :log-events (str (count-pushes) " pushes printed to the console (F12)")))

(defmethod grid/action :web.dev-tools/log-last [app-atom _ _]
  #?(:cljs (js/console.log "[yc-tracking] last push" (tracking/inspector-last->js)))
  (feedback! app-atom :log-last (if (pos? (count-pushes)) "printed to the console" "nothing recorded yet")))

(defmethod grid/action :web.dev-tools/clear-events [app-atom _ _]
  (tracking/clear-inspector-events!)
  (feedback! app-atom :clear-events "cleared")
  (refresh-browser-state! app-atom))

(defmethod grid/action :web.dev-tools/log-datalayer [app-atom _ _]
  (let [events (datalayer-events)]
    #?(:cljs (when events (js/console.table events)))
    (feedback! app-atom :log-datalayer
      (if events
        (str (.-length events) " events printed to the console")
        "window.dataLayer is missing on this page"))))

(defmethod grid/action :web.dev-tools/rearm-purchase [app-atom _ _]
  (let [order-id (str (cmn/curact app-atom [:dev-tools :order-id]))]
    (feedback! app-atom :rearm-purchase
      (cond
        (not (re-matches #"\d+" order-id)) "enter the numeric order id first"
        (pos? (remove-storage-keys! #?(:cljs js/localStorage :clj nil)
                (re-pattern (str "^yc-ga-[a-z_]+-" order-id "$"))))
        (str "re-armed: the next confirmation page for order " order-id " pushes purchase again")
        :else (str "nothing stored for order " order-id " in this browser")))))

(defmethod grid/action :web.dev-tools/rearm-checkout [app-atom _ _]
  (let [n (remove-storage-keys! #?(:cljs js/sessionStorage :clj nil) #"^yc-ck-")]
    (feedback! app-atom :rearm-checkout
      (if (pos? n)
        (str n " checkout step guard" (when (not= 1 n) "s") " removed: the steps fire again in this tab")
        "no checkout step guards in this tab"))))

(rum/defc action-row < rum/reactive [app-atom k label description]
  (let [feedback (cmn/curact app-atom [:dev-tools :feedback k])]
    [:div.u-margin-bottom-10 {:key (name k)}
     (buttons/button-web label
       {:style :secondary
        :size :small
        :on-click #(grid/dispatch-action app-atom (keyword "web.dev-tools" (name k)) {})})
     [:span.yc-paragraph.u-margin-left-10 description]
     (when feedback
       [:span.yc-paragraph.u-margin-left-10 [:strong (str "→ " feedback)]])]))

(rum/defc console-section < rum/reactive [app-atom]
  (let [{:keys [links order-id]} (cmn/curact app-atom [:dev-tools])]
    [:div
     (headings/heading "Console actions" {:type :h2 :size :small})
     [:p.yc-paragraph "Each button runs the matching window.ycTracking call for you; tables land in the browser console (F12)."]
     (action-row app-atom :log-events "Print pushes" "console.table(ycTracking.events()): every dataLayer push the SPA attempted on this page")
     (action-row app-atom :log-last "Print last push" "console.log(ycTracking.last())")
     (action-row app-atom :clear-events "Clear pushes" "ycTracking.clear()")
     (action-row app-atom :log-datalayer "Print GTM's dataLayer" "console.table(dataLayer.filter(e => e.event)): what GTM actually received")
     (action-row app-atom :rearm-checkout "Re-arm checkout steps" "removes the sessionStorage yc-ck-<cart>-step-<n> guards. Per tab: do it here, then follow this link in the same tab:")
     [:p.yc-paragraph.u-margin-bottom-10
      [:a.text-link {:href "/order.htm?action=ShowCart"} "open the cart in this tab"]]
     [:div.u-margin-bottom-10
      [:input
       {:type "text"
        :placeholder "order id"
        :value (or order-id "")
        :on-change #(swap! app-atom assoc-in [:dev-tools :order-id] (.. % -target -value))}]
      " "
      (buttons/button-web "Re-arm purchase push"
        {:style :secondary
         :size :small
         :on-click #(grid/dispatch-action app-atom :web.dev-tools/rearm-purchase {})})
      [:span.yc-paragraph.u-margin-left-10 "removes the localStorage yc-ga-*-<orderId> guards so the confirmation page pushes purchase again"]
      (when-let [feedback (cmn/curact app-atom [:dev-tools :feedback :rearm-purchase])]
        [:span.yc-paragraph.u-margin-left-10 [:strong (str "→ " feedback)]])]
     (headings/heading "Links" {:type :h2 :size :small})
     [:ul
      [:li [:a.text-link {:href (:tag-assistant links) :target "_blank"} "Tag Assistant"] " (Preview the container against this host)"]
      [:li [:a.text-link {:href (:tag-manager links) :target "_blank"} "Tag Manager"]]
      [:li [:a.text-link {:href (:ga4 links) :target "_blank"} "GA4"] " (DebugView while Preview is connected; Realtime for arrivals)"]
      [:li [:a.text-link {:href (:meta-test-events links) :target "_blank"} "Meta Events Manager, Test events"] " (keep it open while testing CAPI)"]
      [:li [:a.text-link {:href (:runbook links) :target "_blank"} "Sandbox runbook"] " (re-clone drill, what the sandbox is)"]]]))

(rum/defc dev-tools-page < rum/reactive
  {:did-mount
   (fn [state]
     (refresh-browser-state! (first (:rum/args state)))
     state)}
  [app-atom]
  [:div {:class (u/bem :yc-page-content [:standard])}
   [:div {:class (u/bem :yc-section [:large])}
    [:div.u-max-width-640
     (headings/heading "Dev tools" {:type :h1 :size :large})
     [:p.yc-paragraph "Staff only. Everything here is read from the server for this environment; the switches are plain browser state."]
     (environment-section app-atom)
     (switches-section app-atom)
     (console-section app-atom)]]])

(rum/defc not-found-page [_]
  [:main#main-content.yc-page-content.yc-page-content--standard
   [:div.yc-section
    (flow/page-not-found-content)]])

(defmethod flow/render-screen :web.dev-tools [app-atom _]
  (website/screen-wrapper app-atom
    (if (cmn/curact app-atom [:dev-tools :not-found?])
      (not-found-page app-atom)
      (dev-tools-page app-atom))
    {}))

(defmethod t/transition-open :web.dev-tools [app-atom _ _]
  (swap! app-atom assoc-in [:dev-tools :gtm-off?] (gtm-off? app-atom))
  (comms/ajax-get :web/dev-tools {}))

(defmethod comms/receive :web/dev-tools [app-atom e]
  (swap! app-atom update :dev-tools merge (select-keys e [:not-found? :dev-mode? :gtm :ga4 :meta :links])))

(defmethod grid/action :web.dev-tools/toggle-gtm [app-atom _ _]
  (set-gtm-off! (not (gtm-off? app-atom)))
  (refresh-browser-state! app-atom))

(defmethod grid/action :web.dev-tools/toggle-inspector [app-atom _ _]
  (tracking/set-inspector! (not (:enabled? (tracking/inspector-status-map))))
  (refresh-browser-state! app-atom))

(defmethod grid/action :web.dev-tools/reset-inspector [app-atom _ _]
  (tracking/clear-inspector-override!)
  (refresh-browser-state! app-atom))
